import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'

import Select from 'react-select'
import {NumericFormat} from 'react-number-format'
import {useAddPackageItem, useDeletePackageItem} from 'src/feature/package/context/useMutation'
import {Error, Success} from 'src/feature/bite/helpers/notification'
import {PackageItemSchema} from '../../../domain'
import {useGetList} from 'src/feature/items/context/useQuery'
import z from 'zod'
import {useItem} from 'src/feature/items/hook'
import Swal from 'sweetalert2'
import {usePackage} from 'src/feature/package/hook'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {ConnectedFocusError} from 'focus-formik-error'
import {useGetActiveListNotPackage} from 'src/feature/items/context/useQuery'

export default function SubscriptionItemsArea({
  intl,
  setFieldValue,
  isEditable,
}: {
  intl: any
  setFieldValue: any
  isEditable: boolean
}) {
  const {mutateAsync: addPackageItem, isLoading: isAddingItem} = useAddPackageItem()
  const {mutateAsync: deletePackageItem} = useDeletePackageItem()
  const {isLoading: isLoadingPackageItemList} = useGetActiveListNotPackage()
  const {listActive: packageItems} = useItem()
  const {detail: initialItems, detailSet} = usePackage()

  const initialValues = {
    credit: '',
    subscriptionItemID: '',
    name: '',
  }

  const validationSchema = Yup.object().shape({
    subscriptionItemID: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    credit: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(1, intl.formatMessage({id: 'GLOBAL.ERROR.MAX_LIMIT_INPUT'}, {length: 1}))
      .max(99999, intl.formatMessage({id: 'GLOBAL.ERROR.MAX_LIMIT_INPUT'}, {length: 99999})),
  })

  const handleDeletePackageItem = async (id) => {
    try {
      Swal.fire({
        title: 'Are you sure you want to delete this item?',
        text: 'This item will be deleted immediately. You can’t undo this action.',
        iconHtml: '<i class="bi bi-x-circle-fill text-danger fs-4x"></i>',
        customClass: {
          icon: 'text-primary border-none border-0',
          title: 'fs-4',
          container: 'py-0 my-0',
          htmlContainer: 'pt-3 mt-3',
          confirmButton: 'me-10',
          actions: 'd-flex justify-content-between',
        },
        showCancelButton: true,
        confirmButtonText: intl.formatMessage({id: 'YES'}),
        cancelButtonText: intl.formatMessage({id: 'NO'}),
        confirmButtonColor: '#5cb85c',
        cancelButtonColor: '#F1416C',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const deleteResponse = await deletePackageItem(id)
          if (deleteResponse) {
            Success(intl.formatMessage({id: 'GLOBAL.SUCCESS.DELETE_ITEM_PACKAGE'}))

            const newItems = initialItems?.items?.filter((item) => item.id !== id)

            setFieldValue('items', newItems)
            detailSet({...initialItems, items: newItems})
          }
        }
      })
    } catch (error) {
      Error(intl.formatMessage({id: 'UPDATE.PRODUCT.ERROR'}))
    }
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={async (values, {setSubmitting, resetForm}) => {
          try {
            await addPackageItem(values)
            resetForm()
            Success(intl.formatMessage({id: 'GLOBAL.SUCCESS.ADD_ITEM_PACKAGE'}))
          } catch (error) {
            console.error('Error adding subscription item:', error)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {(prop) => {
          return (
            <Form className='form d-flex flex-column flex-lg-row fv-plugins-bootstrap5 fv-plugins-framework w-100'>
              <ConnectedFocusError />
              <div className='card card-flush w-100'>
                <div className='card-header'>
                  <div className='card-title m-0'>
                    <div>
                      <h3 className='fw-bold mb-2 mt-5'>
                        <h2>
                          {' '}
                          {intl.formatMessage({
                            id: 'MEMBER.DETAIL.PACKAGE.SUBSCRIBER_PACKAGE_ITEMS',
                          })}
                        </h2>
                      </h3>
                      <p className='text-muted fw-normal fs-6'>
                        Define the services or products included in this membership.
                      </p>
                    </div>
                  </div>
                </div>
                <div className='card-body pt-0 w-100'>
                  <div className='row'>
                    <div className='col-2'>
                      <div className='fv-row fv-plugins-icon-container'>
                        <label className='required form-label'>
                          {intl.formatMessage({
                            id: 'LABEL.ITEM',
                          })}

                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip>
                                Select an active product to be included in the membership.
                              </Tooltip>
                            }
                          >
                            <i className='bi bi-info-circle text-primary ms-1'></i>
                          </OverlayTrigger>
                        </label>

                        <Select
                          isDisabled={!isEditable}
                          id='subscriptionItemID'
                          name='subscriptionItemID'
                          className='custom-select'
                          options={
                            packageItems &&
                            packageItems?.dataList
                              ?.filter(
                                (item) =>
                                  !initialItems?.items
                                    ?.map((item) => item.subscriptionItemID)
                                    .includes(item.itemID)
                              )
                              .map((item) => ({
                                value: item.itemID,
                                label: item.name,
                              }))
                          }
                          value={packageItems?.dataList
                            ?.filter((item: any) => item.itemID === prop.values.subscriptionItemID)
                            ?.map((item) => ({value: item.itemID, label: item.name}))}
                          onChange={(e: any) => {
                            prop.setFieldValue('subscriptionItemID', e.value)
                            prop.setFieldValue('name', e.label)
                          }}
                        />
                        <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                          {prop.errors.subscriptionItemID && prop.touched.subscriptionItemID && (
                            <div className='fv-help-block'>{prop.errors.subscriptionItemID}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='col-2'>
                      <div className='fv-row fv-plugins-icon-container'>
                        <label className='required form-label'>
                          {intl.formatMessage({
                            id: 'LABEL.CREDIT',
                          })}

                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip>
                                Specify the number of credits assigned to the product, determining
                                how many times it can be used within the subscription.
                              </Tooltip>
                            }
                          >
                            <i className='bi bi-info-circle text-primary ms-1'></i>
                          </OverlayTrigger>
                        </label>
                        <NumericFormat
                          name='credit'
                          disabled={!isEditable}
                          readOnly={!isEditable}
                          className='form-control mb-2'
                          maxLength={3}
                          min={1}
                          max={999}
                          decimalScale={0}
                          allowNegative={false}
                          placeholder={intl.formatMessage({
                            id: 'LABEL.CREDIT',
                          })}
                          value={prop.values.credit}
                          onValueChange={({value}) => {
                            prop.setFieldValue('credit', value)
                          }}
                        />
                        <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                          {prop.errors.credit && prop.touched.credit && (
                            <div className='fv-help-block'>{prop.errors.credit}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='col-2'>
                      <div className='d-flex flex-column'>
                        <label className='form-label'>&nbsp; &nbsp;</label>
                        <button
                          disabled={!isEditable || isLoadingPackageItemList || isAddingItem}
                          type='button'
                          className='btn btn-primary'
                          onClick={() => prop.submitForm()}
                        >
                          {intl.formatMessage({id: 'LABEL.ADD_ITEM'})}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='row'>
                    <h5 className='fw-bold mt-5'>
                      {intl.formatMessage({id: 'LABEL.INCLUDED.ITEMS_SERVICES'})}
                    </h5>

                    <div>
                      <table className='table table-striped mt-5'>
                        <thead>
                          <tr className='fw-bold'>
                            <th>ID</th>
                            <th>{intl.formatMessage({id: 'LABEL.ITEM'})}</th>
                            <th>{intl.formatMessage({id: 'LABEL.CREDIT'})}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {initialItems?.items?.map((item, index) => (
                            <tr key={item.id} className='align-items-center'>
                              <td className='w-25'>{item.id}</td>
                              <td>{item.name || '-'}</td>
                              <td>{item.credit}</td>
                              <td>
                                <button
                                  type='button'
                                  disabled={!isEditable}
                                  className='btn btn-danger'
                                  onClick={() => handleDeletePackageItem(item.id)}
                                >
                                  <i className='bi bi-trash'></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
